/**
 * Copy mapping, time estimates, and deep-link routing for Milestone 1 Next Step Cards.
 * Conforms to RDLink_Next_Step_Cards_Copy_Spec.md and MILESTONE_1_FRONTEND_IMPLEMENTATION_PLAN.md
 */

export const getActionRoute = (action) => {
  if (!action) return "/brands/dashboard";
  switch (action.type) {
    case "open_messages":
      return action.rd_id
        ? `/brands/apps/chat/chatBrands/${action.rd_id}`
        : "/brands/apps/chat/chatBrands";
    case "view_applicants":
    case "manage_program":
      return action.campaign_id
        ? `/brands/campaign-applications/${action.campaign_id}`
        : "/brands/outreach-posts";
    case "view_programs":
      return "/brands/outreach-posts";
    case "edit_draft":
      return action.draft_id
        ? `/brands/outreach-posts?editDraft=${action.draft_id}`
        : "/brands/outreach-posts";
    case "preview_post":
      return action.preview_id || action.campaign_id
        ? `/brands/outreach-posts?previewId=${action.preview_id || action.campaign_id}`
        : "/brands/outreach-posts";
    case "create_post":
      return "/brands/outreach-posts?create=true";
    case "edit_post":
      return action.campaign_id
        ? `/brands/outreach-posts?editDraft=${action.campaign_id}`
        : "/brands/outreach-posts";
    case "mark_sent":
      return "/brands/outreach-posts";
    case "finish_profile":
      return (action.field || action.focus)
        ? `/dashboard/brands/edit-profile?focus=${action.field || action.focus}`
        : "/dashboard/brands/edit-profile";
    case "browse_new_rds":
    case "browse_rds":
    case "new_matching_rds": {
      const basePath = "/dashboard/brands/nutritions";
      const params = [];

      const filters = action.filters || {};
      const filterType = (filters.type && filters.type !== "browse_new_rds" && filters.type !== "browse_rds" && filters.type !== "new_matching_rds")
        ? filters.type
        : (action.filter || "new");
      params.push(`filter=${encodeURIComponent(String(filterType).toLowerCase())}`);

      // 1. Specialty / Speciality (sep)
      const speciality =
        filters.speciality ||
        filters.specialty ||
        filters.sep ||
        action.speciality ||
        action.specialty ||
        action.sep ||
        (action.category_type === "speciality" ? action.category_id : null);

      if (speciality !== undefined && speciality !== null) {
        const val = Array.isArray(speciality) ? speciality.join(",") : speciality;
        if (val) {
          params.push(`sep=${encodeURIComponent(val)}`);
        }
      }

      // 2. Industry (ind)
      const industry =
        filters.industry ||
        filters.ind ||
        action.industry ||
        action.ind ||
        (action.category_type === "industry" ? action.category_id : null);

      if (industry !== undefined && industry !== null) {
        const val = Array.isArray(industry) ? industry.join(",") : industry;
        if (val) {
          params.push(`ind=${encodeURIComponent(val)}`);
        }
      }

      // 3. Personal Attributes (pc)
      const personalAttr =
        filters.personal_attr ||
        filters.personal_attribute ||
        filters.pc ||
        action.personal_attr ||
        action.personal_attribute ||
        action.pc ||
        (action.category_type === "personal_attributes" || action.category_type === "personal_attr"
          ? action.category_id
          : null);

      if (personalAttr !== undefined && personalAttr !== null) {
        const val = Array.isArray(personalAttr) ? personalAttr.join(",") : personalAttr;
        if (val) {
          params.push(`pc=${encodeURIComponent(val)}`);
        }
      }

      return `${basePath}?${params.join("&")}`;
    }
    default:
      return "/brands/dashboard";
  }
};

export const formatNextStepCopy = (step) => {
  if (!step) return null;

  const vars = step.variables || {};
  let title = step.title;
  let description = step.body || step.description;
  let ctaLabel = step.cta?.label || step.action?.label;
  let timeEstimate = step.estimated_minutes
    ? `~${step.estimated_minutes} min`
    : (step.time_estimate || null);

  switch (step.type) {
    case "new_applicants": {
      const count = vars.count || 1;
      const prog = vars.program_name || "your program";
      title = title || (count === 1
        ? `Review 1 new applicant for ${prog}`
        : `Review ${count} new applicants for ${prog}`);
      description = description || (count === 1
        ? `1 dietitian applied recently. Review their credentials and respond.`
        : `${count} dietitians applied in the last 48 hours. Review them before the program fills up.`);
      ctaLabel = ctaLabel || "Review applicants";
      timeEstimate = timeEstimate || (vars.count === 1 ? "~2 min" : "~2 min each");
      break;
    }

    case "unread_messages": {
      const rdName = vars.rd_name || vars.rd_first_name || "a dietitian";
      title = title || `Reply to ${rdName}`;
      description = description || (vars.count > 1
        ? `You have ${vars.count} unread messages from RDs waiting for your response.`
        : `New message waiting regarding campaign details and requirements.`);
      ctaLabel = ctaLabel || "Open messages";
      timeEstimate = timeEstimate || "~3 min";
      break;
    }

    case "post_attention": {
      const prog = vars.program_name || "campaign";
      if (vars.variant === "almost_full") {
        title = title || `Post almost full: ${prog}`;
        description = description || `${vars.applicant_count || 0} of ${vars.capacity || 0} spots filled. Review remaining applicants before closing.`;
        ctaLabel = ctaLabel || "Review applicants";
        timeEstimate = timeEstimate || "~3 min";
      } else {
        title = title || `Post closing soon: ${prog}`;
        description = description || `Closes in ${vars.days_until_close || 0} days. Review remaining spots.`;
        ctaLabel = ctaLabel || "Review applicants";
        timeEstimate = timeEstimate || "~2 min";
      }
      break;
    }

    case "draft_post": {
      const prog = vars.program_name || "your draft campaign";
      title = title || `Finish & publish your draft campaign`;
      description = description || `You started '${prog}' recently. Complete details to launch it to RDs.`;
      ctaLabel = ctaLabel || "Post it now";
      timeEstimate = timeEstimate || "~2 min";
      break;
    }

    case "expiring_post": {
      const prog = vars.program_name || "campaign";
      title = title || `Post closing soon: ${prog}`;
      description = description || `This campaign is nearing its application deadline. Extend dates or review remaining spots.`;
      ctaLabel = ctaLabel || "Extend or close";
      timeEstimate = timeEstimate || "~2 min";
      break;
    }

    case "almost_full_post": {
      const prog = vars.program_name || "campaign";
      title = title || `Post almost full: ${prog}`;
      description = description || `Applications are nearing your spot limit. Review candidates or add more slots.`;
      ctaLabel = ctaLabel || "Review or add spots";
      timeEstimate = timeEstimate || "~3 min";
      break;
    }

    case "product_sample": {
      const count = vars.count || 1;
      title = title || `Send sample kits to ${count} accepted RDs`;
      description = description || `Shipping addresses were submitted. Mark as sent once dispatched.`;
      ctaLabel = ctaLabel || "Mark as sent";
      timeEstimate = timeEstimate || "~1 min";
      break;
    }

    case "scheduled_post": {
      const prog = vars.program_name || "post";
      title = title || `Scheduled post goes live soon`;
      description = description || `'${prog}' will be visible to dietitians shortly. Preview before launch.`;
      ctaLabel = ctaLabel || "Preview post";
      timeEstimate = timeEstimate || "~1 min";
      break;
    }

    case "no_active_posts": {
      if (vars.variant === "posting_gap") {
        title = title || `You haven't posted in ${vars.days_since_last_post || 30} days`;
        description = description || `Dietitians check the board weekly. Keep your brand top of mind with a new outreach post.`;
      } else {
        title = title || `Create your first outreach post`;
        description = description || `Reach dietitians for advisory boards, sponsored content, and sample distribution.`;
      }
      ctaLabel = ctaLabel || "Create outreach";
      timeEstimate = timeEstimate || "~3 min";
      break;
    }

    case "incomplete_profile":
    case "profile_completion": {
      const percent = vars.completion_percent ? ` (${vars.completion_percent}%)` : "";
      title = title || `Complete your brand profile${percent}`;
      description = description || `Dietitians check brand profiles before applying. Add your ${vars.first_missing_item || "company bio and details"}.`;
      ctaLabel = ctaLabel || "Finish profile";
      timeEstimate = timeEstimate || "~4 min";
      break;
    }

    case "new_matching_rds":
    case "browse_rds": {
      title = title || `Explore newly registered dietitians`;
      description = description || `${vars.count ? `${vars.count} new registered dietitians` : "New registered dietitians"} matching your brand focus joined RD Link this month.`;
      ctaLabel = ctaLabel || "Browse new RDs";
      timeEstimate = timeEstimate || "~5 min";
      break;
    }

    default:
      break;
  }

  let action = step.action ? { ...step.action } : null;
  if ((step.type === "new_matching_rds" || step.type === "browse_rds" || step.type === "browse_new_rds") && vars.category_type && vars.category_id) {
    action = action || { type: "browse_new_rds" };
    if (!action.filters && !action.category_type) {
      const filterKey =
        vars.category_type === "speciality"
          ? "speciality"
          : vars.category_type === "industry"
          ? "industry"
          : "personal_attr";
      action.filters = {
        type: "New",
        [filterKey]: [vars.category_id]
      };
      action.category_type = vars.category_type;
      action.category_id = vars.category_id;
    }
  }

  const isNoActivePosts = step.type === "no_active_posts";
  const templates = isNoActivePosts
    ? (step.templates || IDEA_CHIP_TEMPLATES)
    : (step.templates || null);

  return {
    ...step,
    title,
    description,
    ctaLabel,
    timeEstimate,
    templates,
    ...(action ? { action } : {})
  };
};

/**
 * Filter and prioritize next steps:
 * 1. Almost-full variant wins over expiring variant for the same campaign.
 * 2. Low-priority group (profile_completion, browse_rds) shows at most one card.
 * 3. Caps visible steps at 4 cards (1 Hero + 3 Secondary).
 */
export const filterAndPrioritizeNextSteps = (steps = []) => {
  if (!Array.isArray(steps) || !steps.length) return [];

  const campaignMap = new Map();
  const nonCampaignSteps = [];

  for (const step of steps) {
    const campaignId = step.variables?.campaign_id || step.action?.campaign_id;
    if (campaignId) {
      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, []);
      }
      campaignMap.get(campaignId).push(step);
    } else {
      nonCampaignSteps.push(step);
    }
  }

  const resolvedCampaignSteps = [];
  for (const [_, campSteps] of campaignMap.entries()) {
    const hasAlmostFull = campSteps.find((s) => s.type === "almost_full_post");
    const hasExpiring = campSteps.find((s) => s.type === "expiring_post");

    if (hasAlmostFull && hasExpiring) {
      resolvedCampaignSteps.push(
        ...campSteps.filter((s) => s.type !== "expiring_post")
      );
    } else {
      resolvedCampaignSteps.push(...campSteps);
    }
  }

  const allFiltered = [...resolvedCampaignSteps, ...nonCampaignSteps];

  const lowPriorityTypes = ["profile_completion", "browse_rds"];
  let lowPriorityCount = 0;
  const result = [];

  for (const step of allFiltered) {
    if (lowPriorityTypes.includes(step.type)) {
      if (lowPriorityCount === 0) {
        result.push(step);
        lowPriorityCount++;
      }
    } else {
      result.push(step);
    }
  }

  return result.slice(0, 4);
};

/**
 * Format campaign capacity:
 * Finite: "[applicants] / [submission_limit]"
 * Unlimited: "Unlimited"
 */
export const formatCapacity = (applicants = 0, limit = null) => {
  if (!limit || limit <= 0) return "Unlimited";
  return `${applicants} / ${limit}`;
};

/**
 * Renewal notification threshold:
 * Visible within 60 days, hidden at 61+ days.
 */
export const isRenewalDue = (daysUntilRenewal) => {
  if (daysUntilRenewal === undefined || daysUntilRenewal === null) return false;
  return Number(daysUntilRenewal) <= 60;
};

/**
 * Missing-profile closing-line precedence:
 * Precedence: logo -> description -> attachments -> social
 */
export const getMissingProfilePrecedence = (profile = {}) => {
  if (!profile.logo) return "logo";
  if (!profile.description && !profile.about_company) return "description";
  if (!profile.attachments || !profile.attachments.length) return "attachments";
  if (!profile.social && !profile.instagram && !profile.facebook) return "social";
  return null;
};

/**
 * Outreach Idea Chip Templates
 */
export const IDEA_CHIP_TEMPLATES = [
  {
    id: "product_gifting",
    label: "Product Gifting",
    route: "/brands/outreach-posts?create=true&template=product_gifting"
  },
  {
    id: "sponsored_content",
    label: "Sponsored Content",
    route: "/brands/outreach-posts?create=true&template=sponsored_content"
  },
  {
    id: "advisory_board",
    label: "Advisory Board",
    route: "/brands/outreach-posts?create=true&template=advisory_board"
  },
  {
    id: "affiliate_program",
    label: "Ambassador Program",
    route: "/brands/outreach-posts?create=true&template=affiliate_program"
  }
];

